import { Component, ChangeDetectionStrategy, Input, Output, EventEmitter, inject, ChangeDetectorRef } from '@angular/core';
import type { OnChanges, SimpleChanges , OnInit, OnDestroy} from '@angular/core';
import { Subject, takeUntil, finalize, catchError, of, combineLatest, forkJoin } from 'rxjs';
import type { IUser, SystemRole } from '../../shared/models/user.model';
import type { ActiveCycleData, PastCycleData } from '../../shared/models/cycle-management.models';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import type { PageEvent } from '@angular/material/paginator';
import { MatPaginatorModule } from '@angular/material/paginator';
import { CustomTooltipDirective } from '../../shared/directives/custom-tooltip.directive';
import { DefaultImageDirective } from '../../shared/directives/default-image.directive';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import type { SubManager } from '../../core/services/manager-dashboard.service';
import { ManagerDashboardService } from '../../core/services/manager-dashboard.service';
import { SnackbarService } from '../../core/services/snackbar.service';
import { AuthService } from '../../core/services/auth.service';
import { UserInitialsPipe } from '../../shared/pipes/user-initials.pipe';
import { getProfileImageUrl } from '../../shared/utils/profile-image.util';
import config from '../../../config';

@Component({
  selector: 'app-manager-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    NgOptimizedImage,
    RouterModule,
    MatIconModule,
    MatButtonModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    CustomTooltipDirective,
    UserInitialsPipe,
    DefaultImageDirective
  ],
  templateUrl: './manager-dashboard.component.html',
  styleUrls: ['./manager-dashboard.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ManagerDashboardComponent implements OnInit, OnDestroy, OnChanges {
  userName = '';
  @Input() userRoleInput!: string;
  @Input() currentUserInput!: IUser;
  @Output() readonly loadingEvent = new EventEmitter<boolean>();
  market = '';
  businessUnit = '';
  marketId: number | undefined = undefined;

  // Reports dashboard URL
  reportsDashboardUrl = config.environment_name === 'dev'
    ? 'https://app.powerbi.com/groups/3b6b1601-c670-4e6e-b863-f1835a30051a/reports/ba26883d-18c6-40d8-b881-4512b3328f99/b7690b443e0a86b4b19a?experience=power-bi'
    : 'https://app.powerbi.com/Redirect?action=OpenApp&appId=5619a16c-49cd-440c-9cdb-72be2d9f7941&ctid=af8e89a3-d9ac-422f-ad06-cc4eb4214314&experience=power-bi';

  // Services
  private readonly managerDashboardService = inject(ManagerDashboardService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);

  // Profile image utility
  getProfileImageUrl = getProfileImageUrl;

  // Component state
  private readonly destroy$ = new Subject<void>();
  private isDataLoaded = false; // Guard to prevent duplicate initial loads
  private previousMarket = ''; // Track previous market to detect context changes
  private previousBusinessUnit = ''; // Track previous business unit to detect context changes
  isLoading = false;
  isLoadingPastCycles = false;
  isLoadingSubManagers = false;
  error: string | null = null;

  // Data properties
  activeCycles: ActiveCycleData[] = [];
  pastCycles: PastCycleData[] = [];
  subManagers: SubManager[] = [];

  // Pagination
  totalPastCycles = 0;
  pageSize = 10;
  pageIndex = 0;
  pageSizeOptions = [5, 10, 25];

  ngOnInit(): void {
    // Subscribe to user data and selected role CONTEXT (includes market_id, business_unit_id, etc.)
    combineLatest([
      this.authService.userData$,
      this.authService.selectedRoleContext$
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([userData, selectedRoleContext]) => {
        if (userData) {
          this.userName = `${userData.first_name ?? ''} ${userData.last_name ?? ''}`.trim();

          // Store previous values to detect changes
          const previousMarket = this.market;
          const previousBusinessUnit = this.businessUnit;
          const previousMarketId = this.marketId;

          // Get market and business unit from the FULL selected role context
          if (selectedRoleContext) {
            // Use the exact role context with specific market_id and business_unit_id
            this.market = selectedRoleContext.market_name || userData.market || 'N/A';
            this.marketId = selectedRoleContext.market_id;
            // Check for business_unit_name, then business_units array, then fallback
            this.businessUnit = this.getBusinessUnitName(selectedRoleContext, userData);
          } else if (userData.system_role_info && userData.system_role_info.length > 0) {
            // Fallback to first role if no context selected yet
            const firstRole = userData.system_role_info[0];
            this.market = firstRole.market_name || userData.market || 'N/A';
            this.marketId = firstRole.market_id;
            this.businessUnit = this.getBusinessUnitName(firstRole, userData);
          } else {
            this.market = userData.market || 'N/A';
            this.businessUnit = userData.business_unit || 'N/A';
          }

          // Detect if market or business unit has actually changed
          const hasContextChanged =
            (previousMarket !== '' && previousMarket !== this.market) ||
            (previousBusinessUnit !== '' && previousBusinessUnit !== this.businessUnit);

          // Load dashboard data for the first time after marketId is set
          if (this.currentUserInput && !this.isDataLoaded && this.marketId !== undefined) {
            console.log('Loading dashboard data with marketId:', this.marketId);
            this.loadDashboardData();
          }
          // Reload dashboard data when role context changes (market/business unit switch)
          else if (this.currentUserInput && this.isDataLoaded && hasContextChanged) {
            console.log('Role context changed, reloading dashboard data...', {
              previousMarket,
              newMarket: this.market,
              previousBusinessUnit,
              newBusinessUnit: this.businessUnit,
              previousMarketId,
              newMarketId: this.marketId
            });
            this.isDataLoaded = false; // Reset flag to allow reload
            this.loadDashboardData();
          }

          this.cdr.markForCheck();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(_changes: SimpleChanges): void {
    // Only load data if it hasn't been loaded yet and marketId is available
    if (this.currentUserInput && !this.isDataLoaded && this.marketId !== undefined) {
      this.loadDashboardData();
    }
  }

  private loadDashboardData(): void {
    if (!this.currentUserInput) {
      return;
    }

    // Wait for marketId to be available before loading data
    if (this.marketId === undefined) {
      console.log('Waiting for marketId to be available...');
      return;
    }

    // Mark as loaded to prevent duplicate calls
    this.isDataLoaded = true;

    this.isLoading = true;
    this.loadingEvent.emit(true);
    this.error = null;

    console.log('Loading all dashboard data for user:', this.currentUserInput.id, 'with marketId:', this.marketId);

    // Load critical data (cycles) first
    forkJoin({
      activeCycles: this.managerDashboardService.getActiveCycles(this.currentUserInput, this.marketId).pipe(
        catchError(error => {
          console.error('Error loading active cycles:', error);
          return of([]);
        })
      ),
      paginatedPastCycles: this.managerDashboardService.getPaginatedPastCycles(
        this.currentUserInput.id,
        this.pageSize,
        this.pageIndex,
        this.marketId
      ).pipe(
        catchError(error => {
          console.error('Error loading past cycles:', error);
          return of({ cycles: [], totalCount: 0, pageSize: this.pageSize, pageNumber: this.pageIndex, totalPages: 0 });
        })
      )
    })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoading = false;
          this.loadingEvent.emit(false);
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (result) => {
          // Active cycles
          this.activeCycles = result.activeCycles;

          // Paginated past cycles
          this.pastCycles = result.paginatedPastCycles.cycles;
          this.totalPastCycles = result.paginatedPastCycles.totalCount;

          console.log('Critical dashboard data loaded successfully');
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Critical error loading dashboard:', error);
          this.error = 'Failed to load dashboard data. Please try again.';
          this.snackbarService.showError('Failed to load dashboard data');
        }
      });

    // Load sub-managers independently (non-blocking, skip global loader)
    this.isLoadingSubManagers = true;
    this.managerDashboardService.getSubManagers(this.currentUserInput.prid, this.marketId!, true)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoadingSubManagers = false;
          this.cdr.markForCheck();
        }),
        catchError(error => {
          console.error('Error loading sub-managers:', error);
          return of([]);
        })
      )
      .subscribe({
        next: (subManagers) => {
          // Sub-managers with profile images, excluding the logged-in user
          // Only show managers registered in the system
          this.subManagers = subManagers
            .filter(manager => manager.prid.toLowerCase() !== this.currentUserInput.prid.toLowerCase()) // Exclude logged-in user
            .filter(manager => manager.is_system_manager) // Only show registered managers
            .map(manager => ({
              ...manager,
              profileUrl: getProfileImageUrl(manager.prid)
            }));

          console.log('Sub-managers loaded successfully');
          this.cdr.markForCheck();
        }
      });
  }


  private loadPaginatedPastCycles(): void {
    if (!this.currentUserInput) return;

    // Wait for marketId to be available before loading data
    if (this.marketId === undefined) {
      console.log('Waiting for marketId to be available for past cycles...');
      return;
    }

    this.isLoadingPastCycles = true;

    console.log('Loading past cycles for user:', this.currentUserInput.id, 'with marketId:', this.marketId);

    this.managerDashboardService.getPaginatedPastCycles(
      this.currentUserInput.id,
      this.pageSize,
      this.pageIndex,
      this.marketId
    )
      .pipe(
        takeUntil(this.destroy$),
        catchError(error => {
          console.error('Error loading past cycles:', error);
          this.snackbarService.showError('Failed to load past cycles');
          return of({ cycles: [], totalCount: 0, pageSize: this.pageSize, pageNumber: this.pageIndex, totalPages: 0 });
        }),
        finalize(() => {
          this.isLoadingPastCycles = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe(data => {
        this.pastCycles = data.cycles;
        this.totalPastCycles = data.totalCount;
        this.cdr.markForCheck();
      });
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadPaginatedPastCycles();
  }

  onOpenCycle(cycleId: number): void {
    if (!this.currentUserInput?.id) {
      this.snackbarService.showError('User information not available');
      return;
    }
    // Navigate to manager-form/activity/:cycleId for active cycles
    this.router.navigate(['/manager-form/activity', cycleId]);
  }

  onViewCycle(cycleId: number): void {
    if (!this.currentUserInput?.id) {
      this.snackbarService.showError('User information not available');
      return;
    }
    // Navigate to manager-cycle-form for viewing cycle details
    this.router.navigate(['/manager-form/activity', cycleId]);
  }

  refreshData(): void {
    this.isDataLoaded = false; // Reset flag to allow reload
    this.loadDashboardData();
  }

  /**
   * Navigate to manager view page to see specific manager's cycles
   */
  onViewSubManager(manager: SubManager): void {
    this.router.navigate(['/sub-manager-cycles'], {
      queryParams: {
        prid: manager.prid
      }
    });
  }

  // Helper methods for template
  trackByManager = (index: number, item: SubManager): number => item.id;

  /**
   * Get business unit name from role context, handling both single and array formats
   */
  private getBusinessUnitName(role: SystemRole, userData: IUser): string {
    // First try direct business_unit_name property
    if (role.business_unit_name) {
      return role.business_unit_name;
    }
    // Then try business_units array
    if (role.business_units && role.business_units.length > 0) {
      // Return first business unit name, or join if multiple
      if (role.business_units.length === 1) {
        return role.business_units[0].business_unit_name;
      }
      return role.business_units.map(bu => bu.business_unit_name).join(', ');
    }
    // Fallback to userData
    return userData.business_unit || 'N/A';
  }
}
